/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * Responsive design utilities for detecting screen size and adapting layouts.
 */
object ResponsiveUtils {

    // Breakpoints
    const val MOBILE_BREAKPOINT = 768
    const val TABLET_BREAKPOINT = 1024

    /**
     * Check if the current viewport is mobile-sized (< 768px)
     */
    fun isMobile(): Boolean {
        return window.innerWidth < MOBILE_BREAKPOINT
    }

    /**
     * Check if the current viewport is tablet-sized (768px - 1023px)
     */
    fun isTablet(): Boolean {
        return window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < TABLET_BREAKPOINT
    }

    /**
     * Check if the current viewport is desktop-sized (>= 1024px)
     */
    fun isDesktop(): Boolean {
        return window.innerWidth >= TABLET_BREAKPOINT
    }

    /**
     * Get the current device type as a string
     */
    fun getDeviceType(): DeviceType {
        return when {
            isMobile() -> DeviceType.MOBILE
            isTablet() -> DeviceType.TABLET
            else -> DeviceType.DESKTOP
        }
    }

    /**
     * Add a listener for viewport size changes.
     * Returns a function to remove the listener.
     */
    fun onResize(callback: (DeviceType) -> Unit): () -> Unit {
        var lastDeviceType = getDeviceType()

        val listener: (Event) -> Unit = {
            val newDeviceType = getDeviceType()
            if (newDeviceType != lastDeviceType) {
                lastDeviceType = newDeviceType
                callback(newDeviceType)
            }
        }

        window.addEventListener("resize", listener)

        return {
            window.removeEventListener("resize", listener)
        }
    }

    /**
     * Get responsive padding based on device type
     */
    fun getResponsivePadding(): String {
        return if (isMobile()) "16px" else "32px"
    }

    /**
     * Get responsive gap for flex/grid layouts
     */
    fun getResponsiveGap(): String {
        return if (isMobile()) "12px" else "24px"
    }
}

enum class DeviceType {
    MOBILE,
    TABLET,
    DESKTOP
}
